import { useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  message, 
  Space,
  Typography,
  Divider,
  Checkbox,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  BankOutlined, 
  PhoneOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../lib/supabase';

const { Title, Text } = Typography;

export default function CreateUserModal({ visible, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createCompany, setCreateCompany] = useState(true);
  const [sameAsContact, setSameAsContact] = useState(true);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      setLoading(true);
      
      // Prepare data for API call
      const userData = {
        email: values.email,
        voornaam: values.voornaam,
        achternaam: values.achternaam,
        telefoon: values.telefoon
      };

      // Add company data if enabled
      if (createCompany) {
        userData.bedrijfsnaam = values.bedrijfsnaam;
        userData.kvk_nummer = values.kvk_nummer;
        userData.btw_nummer = values.btw_nummer;
        userData.adres = values.adres;
        userData.postcode = values.postcode;
        userData.stad = values.stad;
        
        // Financial contact info
        userData.voornaam_financieel = values.voornaam_financieel;
        userData.achternaam_financieel = values.achternaam_financieel;
        userData.telefoon_financieel = values.telefoon_financieel;
        userData.email_financieel = values.email_financieel;
      }
      
      const { data: session } = await supabase.auth.getSession();
      
      // Always use the correct API route path regardless of environment
      const apiUrl = '/api/admin/create-user';
      
      console.log('Using API URL:', apiUrl);
      
      // Create user via API endpoint
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.session?.access_token || ''}`
        },
        body: JSON.stringify(userData)
      });
      
      // Log the raw response for debugging
      console.log('API response status:', response.status, response.statusText);
      const responseText = await response.text();
      console.log('API response body:', responseText);
      
      if (!response.ok) {
        let errorMessage = 'Failed to create user';
        
        try {
          // Only try to parse as JSON if there's content
          if (responseText && responseText.trim()) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            errorMessage = `${response.status} ${response.statusText}`;
          }
        } catch (jsonError) {
          // If JSON parsing fails, use the status text
          console.error('Error parsing error response:', jsonError);
          errorMessage = `${response.status} ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }
      
      let successData = {};
      try {
        // Only try to parse as JSON if there's content
        if (responseText && responseText.trim()) {
          successData = JSON.parse(responseText);
        }
      } catch (jsonError) {
        console.warn('Error parsing success response:', jsonError);
      }
      
      console.log('User created successfully:', successData);
      
      message.success(`Gebruiker ${values.email} succesvol aangemaakt`);
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Error creating user:', error);
      message.error(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Handle same contact info checkbox
  const handleSameAsContactChange = (e) => {
    const checked = e.target.checked;
    setSameAsContact(checked);
    
    if (checked) {
      const values = form.getFieldsValue();
      form.setFieldsValue({
        voornaam_financieel: values.voornaam,
        achternaam_financieel: values.achternaam,
        telefoon_financieel: values.telefoon,
        email_financieel: values.email
      });
    }
  };

  // Reset form when modal is closed
  const handleCancel = () => {
    form.resetFields();
    setCreateCompany(true);
    setSameAsContact(true);
    onCancel();
  };

  return (
    <Modal
      title="Nieuwe klant aanmaken"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Annuleren
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading} 
          onClick={handleSubmit}
        >
          Aanmaken
        </Button>
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          land: 'Nederland'
        }}
      >
        <Title level={5}>Contactgegevens</Title>
        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'Vul een e-mailadres in' },
            { type: 'email', message: 'Vul een geldig e-mailadres in' }
          ]}
        >
          <Input 
            prefix={<MailOutlined />} 
            placeholder="E-mailadres" 
          />
        </Form.Item>

        <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
          <Form.Item
            name="voornaam"
            rules={[{ required: false, message: 'Vul een voornaam in' }]}
            style={{ flex: 1 }}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Voornaam" 
            />
          </Form.Item>

          <Form.Item
            name="achternaam"
            rules={[{ required: false, message: 'Vul een achternaam in' }]}
            style={{ flex: 1 }}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Achternaam" 
            />
          </Form.Item>
        </Space>

        <Form.Item
          name="telefoon"
          rules={[{ required: false, message: 'Vul een telefoonnummer in' }]}
        >
          <Input 
            prefix={<PhoneOutlined />} 
            placeholder="Telefoonnummer" 
          />
        </Form.Item>

        <Divider />
        
        <Form.Item 
          name="createCompany" 
          valuePropName="checked"
          style={{ marginBottom: 8 }}
        >
          <Checkbox 
            checked={createCompany} 
            onChange={(e) => setCreateCompany(e.target.checked)}
          >
            <Space>
              Bedrijfsgegevens toevoegen
              <Tooltip title="Indien aangevinkt, wordt het gebruikersaccount aangemaakt met bedrijfsgegevens">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          </Checkbox>
        </Form.Item>

        {createCompany && (
          <>
            <Title level={5}>Bedrijfsgegevens</Title>
            <Form.Item
              name="bedrijfsnaam"
              rules={[{ required: false, message: 'Vul een bedrijfsnaam in' }]}
            >
              <Input 
                prefix={<BankOutlined />} 
                placeholder="Bedrijfsnaam" 
              />
            </Form.Item>

            <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
              <Form.Item
                name="kvk_nummer"
                rules={[{ required: false, message: 'Vul een KVK-nummer in' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="KVK-nummer" />
              </Form.Item>

              <Form.Item
                name="btw_nummer"
                rules={[{ required: false, message: 'Vul een BTW-nummer in' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="BTW-nummer" />
              </Form.Item>
            </Space>

            <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
              <Form.Item
                name="adres"
                rules={[{ required: false, message: 'Vul een adres in' }]}
                style={{ flex: 2 }}
              >
                <Input placeholder="Adres" />
              </Form.Item>

              <Form.Item
                name="postcode"
                rules={[{ required: false, message: 'Vul een postcode in' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="Postcode" />
              </Form.Item>
            </Space>

            <Form.Item
              name="stad"
              rules={[{ required: false, message: 'Vul een stad in' }]}
            >
              <Input placeholder="Stad" />
            </Form.Item>

            <Form.Item name="land" hidden>
              <Input />
            </Form.Item>

            <Divider />
            
            <Title level={5}>Financiële contactgegevens</Title>
            
            <Form.Item name="sameAsContact" valuePropName="checked" style={{ marginBottom: 16 }}>
              <Checkbox 
                checked={sameAsContact} 
                onChange={handleSameAsContactChange}
              >
                Dezelfde gegevens als hierboven gebruiken
              </Checkbox>
            </Form.Item>

            <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
              <Form.Item
                name="voornaam_financieel"
                rules={[{ required: false, message: 'Vul de voornaam van uw financieel contactpersoon in' }]}
                style={{ flex: 1 }}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Voornaam" 
                />
              </Form.Item>

              <Form.Item
                name="achternaam_financieel"
                rules={[{ required: false, message: 'Vul de achternaam van uw financieel contactpersoon in' }]}
                style={{ flex: 1 }}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Achternaam" 
                />
              </Form.Item>
            </Space>

            <Form.Item
              name="telefoon_financieel"
              rules={[{ required: false, message: 'Vul het telefoonnummer van uw financieel contactpersoon in' }]}
            >
              <Input 
                prefix={<PhoneOutlined />} 
                placeholder="Telefoonnummer" 
              />
            </Form.Item>

            <Form.Item
              name="email_financieel"
              rules={[
                { required: false, message: 'Vul het e-mailadres van uw financieel contactpersoon in' },
                { type: 'email', message: 'Vul een geldig e-mailadres in' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="E-mailadres" 
              />
            </Form.Item>
          </>
        )}
      </Form>
      
      <Text type="secondary">
        Opmerking: Er wordt een uitnodigingsmail naar deze gebruiker gestuurd. Ze moeten op de link klikken en een wachtwoord instellen om de registratie te voltooien.
      </Text>
    </Modal>
  );
} 